import { AppDispatch } from 'src/ui/app/store';
import { queueNotification } from 'src/ui/features/notifications';

type BulkResult = {
  success: boolean;
  error?: string | null;
};

type BulkResponse<T> = {
  error?: string | null;
  results?: Array<T | null> | null;
};

export const handleMutationResult = <T extends BulkResult>(
  dispatch: AppDispatch,
  response: BulkResponse<T> | null | undefined,
  action: string,
  identifiers: string[],
  getIdentifier: (result: T) => string
): boolean => {
  if (response?.error) {
    dispatch(
      queueNotification(
        `Error ${action}s ${identifiers.join(', ')}: ${response.error}`
      )
    );
    return false;
  }

  let didFail = false;
  response?.results?.forEach((result) => {
    if (result && !result.success) {
      didFail = true;
      dispatch(
        queueNotification(
          `Error ${action} ${getIdentifier(result)}: ${result.error}`
        )
      );
    }
  });
  return !didFail;
};

export const handleMutationError = (
  dispatch: AppDispatch,
  err: unknown,
  action: string,
  identifiers: string[]
) => {
  const errorMsg = typeof err === 'string' ? err : (err as Error)?.message;
  dispatch(
    queueNotification(`Error ${action}s ${identifiers.join(', ')}: ${errorMsg}`)
  );
};

export const runSequentially = (
  steps: Array<{
    enabled: any;
    run: () => Promise<boolean>;
  }>
): Promise<boolean> =>
  steps.reduce<Promise<boolean>>(async (prev, { enabled, run }) => {
    const prevSuccess = await prev;
    if (!prevSuccess || !enabled) {
      return prevSuccess;
    }

    try {
      return await run();
    } catch {
      return false;
    }
  }, Promise.resolve(true));
